'use client';

import type { ReactNode } from 'react';
import { fetchGoodsList } from '@/api/product';
import {
  IGoodsListPageInitialData,
  IGroupInfo,
  IProductListApiParams,
  IProductListApiResponse,
  IProductListItem,
} from '@/api/types/product';
import Breadcrumbs from '@/components/Breadcrumbs';
import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { SubscribeBar, useComplianceSubscriptionDisplay } from '@/components/Subscribe';
import { resolveFaceWidthRecommendation } from '@/domain/lens/measurement';
import { useActiveFaceMeasurement } from '@/domain/lens/measurement/client';
import { useClientOnly } from '@/hooks/useClientOnly';
import { useUserIdentityChange } from '@/hooks/useUserIdentityChange';
import { useStore } from '@/providers/app-store-provider';
import { SpmScopeProvider } from '@/reporting/spm';
import { useSpmTerminalKind } from '@/reporting/spm/spm-dom';
import { PRODUCT_LIST_APP_SPM_CODES } from '@/reporting/spm/codes/product-list-app';
import { PRODUCT_LIST_PC_SPM_CODES } from '@/reporting/spm/codes/product-list-pc';
import { PRODUCT_LIST_WAP_SPM_CODES } from '@/reporting/spm/codes/product-list-wap';
import { withGoodsListPage } from '@/reporting/spm/goods-list-page';
import { setGoodsListSpmGroup } from '@/reporting/spm/goods-list-group';
import { colors } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { DEFAULT_SORT_VALUE } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { getGroupInfo } from '@/utils/feature-value';
import { transformFilterParams } from '@/utils/goods-list-utils';
import { isEmpty, setSession } from '@/utils/tools';
import { useTranslations } from '@/i18n/use-translations';
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import FaqPart from '../components/FaqPart';
import GoodsFilter from '../components/GoodsFilter';
import GoodsListSeoBlock from '../components/GoodsListSeoBlock';
import GoodsListSeoFooterSlot from '../components/GoodsListSeoFooterSlot';
import ClientSeoBlockWrapper from '../components/ClientSeoBlockWrapper';
import ClientFaqPartWrapper from '../components/ClientFaqPartWrapper';
import GroupBanner from '../components/GroupBanner';
import ProductList from '../components/ProductList';
import SortFilterBar from '../components/SortFilterBar';
import { InlineProductGridSkeleton } from '../components/GoodsListSkeleton';
import {
  createLegacyGoodsListSeoGroup,
  type GoodsListSeoFaqRendering,
  runWithGoodsListSeoFooterRefresh,
} from '../components/goodsListSeoFooter.utils';
import { useGoodsListTrack } from '../hooks/useGoodsListTrack';
import { toGoodsListItemsDto } from '../utils/goods-list-client-dto';
import { useListNavigationScroll } from '../hooks/useListNavigationScroll';
import { useKlarnaPaymentPromotion } from '@/components/Pay/KlarnaPromotion/useKlarnaPaymentPromotion';

interface GoodsListPageClientProps {
  initialData: IGoodsListPageInitialData;
  searchParams?: {
    sort?: string;
    filter?: string;
    adp?: string;
    page?: string;
  };
  isBot?: boolean;
  seoFaqRendering?: GoodsListSeoFaqRendering;
  seoFooter?: ReactNode;
}

export default function GoodsListPageClient({
  initialData,
  searchParams,
  isBot = false,
  seoFaqRendering = 'legacy',
  seoFooter,
}: GoodsListPageClientProps) {
  const params = useParams();
  const router = useRouter();
  const pathname = usePathname();
  const searchParamsFromHook = useSearchParams();
  const faceMeasurement = useActiveFaceMeasurement();
  const t = useTranslations('goodsList');
  // 解析当前URL参数
  const currentSort = searchParams?.sort || DEFAULT_SORT_VALUE;
  const currentPage = parseInt(searchParams?.page || '1', 10);

  // 参数
  const limit = 36;
  const listId = params.id as string;

  // 使用useMemo稳定currentFilter的引用，避免无限循环
  const currentFilter = useMemo<Record<string, any>>(() => {
    try {
      return searchParams?.filter ? JSON.parse(searchParams.filter) : {};
    } catch (error) {
      logger.warn('解析筛选参数失败:', searchParams?.filter, error);
      return {};
    }
  }, [searchParams?.filter]);

  // Store
  const currency = useStore((state) => state.currency);
  const publicResourceData = useStore((state) => state.publicResourceData);
  const setIsShowFooterNav = useStore((state) => state.setIsShowFooterNav);
  const waiMode = useStore((state) => state.waiMode);
  const storeToken = useStore((state) => state.token);
  const clientTokenReady = useStore((state) => state.clientTokenReady);
  const setGoodsListKey = useStore((state) => state.setGoodsListKey);
  const setGoodsListCache = useStore((state) => state.setGoodsListCache);
  const getGoodsListCache = useStore((state) => state.getGoodsListCache);
  const productRequestContextKey = JSON.stringify([
    currency.name,
    String(listId),
    currentSort,
    currentPage,
    searchParams?.filter ?? '',
    searchParams?.adp ?? '',
  ]);
  const initialDataRef = useRef(initialData);
  const productRequestContextKeyRef = useRef(productRequestContextKey);
  const latestProductRequestIdRef = useRef(0);
  const listSectionRef = useRef<HTMLElement>(null);
  const productGridRef = useRef<HTMLDivElement>(null);
  const { isShowKlarnaListBanner } = useKlarnaPaymentPromotion();

  // Store Key
  const goodsListKey = useMemo(
    () => `${currency.name}@${listId}@${storeToken || 'guest'}?${searchParamsFromHook.toString()}`,
    [currency.name, listId, searchParamsFromHook, storeToken],
  );
  const cacheData = useMemo(
    () => (clientTokenReady ? getGoodsListCache(goodsListKey) : {}),
    [clientTokenReady, getGoodsListCache, goodsListKey],
  );

  // 基础状态（来自服务端）
  const [groupInfo, setGroupInfo] = useState<IGroupInfo>(initialData.group);
  const [listData, setListData] = useState<IProductListItem[]>(
    cacheData.listData || initialData.list,
  );
  const [total, setTotal] = useState(initialData.total);
  const [allTotal, setAllTotal] = useState(initialData.all_total);
  const [imageRule, setImageRule] = useState(initialData.image_rule);
  const [imageHoverRule, setImageHoverRule] = useState(initialData.hover_image_rule);
  const [noData, setNoData] = useState(initialData.noData);

  // UI相关状态
  const [loading, setLoading] = useState(false);
  const [isDataUpdating, setIsDataUpdating] = useState(false);
  const [isNavigationPending, startNavigation] = useTransition();
  const [reservedGridHeight, setReservedGridHeight] = useState<number>();
  // 占位保留到页面数据同步完成，避免先挂载「新路由参数 + 旧商品」的中间状态。
  const isListUpdating = isDataUpdating || isNavigationPending || reservedGridHeight !== undefined;
  const [noMoreShow, setNoMoreShow] = useState(cacheData.noMoreShow || initialData.noMoreShow);
  const [infiniteScrollPage, setInfiniteScrollPage] = useState(
    cacheData.infiniteScrollPage || currentPage,
  );
  const [column, setColumn] = useState(cacheData.column || 2);
  const [showFilter, setShowFilter] = useState(cacheData.showFilter || false);
  const filterDrawerContentId = useId();
  const checkedScrollListIdRef = useRef<string | number | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(
    cacheData.expandedSections || {},
  );

  // 弹窗模式下的临时筛选状态
  const [tempFilter, setTempFilter] = useState<Record<string, any>>({});
  const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;
  const spmRouteData = useRef({ pathname, initialData });
  const isSpmGroupPending =
    pathname !== spmRouteData.current.pathname && initialData === spmRouteData.current.initialData;
  const spmGroupInfo =
    pathname !== spmRouteData.current.pathname && initialData !== spmRouteData.current.initialData
      ? initialData.group
      : groupInfo;
  useEffect(() => {
    if (!isSpmGroupPending) spmRouteData.current = { pathname, initialData };
  }, [pathname, initialData, isSpmGroupPending]);
  // 仅商品列表路由提供页面级 SPM 作用域（覆盖筛选区/排序栏/FAQ/SEO 内容/面包屑）；
  // product-list（人群推荐列表）等复用本组件的路由不注入，共享组件保持原有行为。
  const spmListTerminalKind = useSpmTerminalKind();
  const listPlatform = isWeb ? 'pc' : spmListTerminalKind === 'app' ? 'app' : 'wap';
  const spmPage = `product_list_${spmGroupInfo.id}_${listPlatform}`;
  const spmCodes = useMemo(
    () =>
      withGoodsListPage(
        isWeb
          ? PRODUCT_LIST_PC_SPM_CODES
          : listPlatform === 'app'
            ? PRODUCT_LIST_APP_SPM_CODES
            : PRODUCT_LIST_WAP_SPM_CODES,
        spmGroupInfo.id,
        listPlatform,
      ),
    [isWeb, listPlatform, spmGroupInfo.id],
  );
  useEffect(() => {
    if (!pathname?.startsWith('/goods-list/') || isSpmGroupPending || !spmGroupInfo?.id) return;
    return setGoodsListSpmGroup(pathname, String(spmGroupInfo.id));
  }, [pathname, isSpmGroupPending, spmGroupInfo.id]);
  const spmScope =
    pathname?.startsWith('/goods-list') && !isSpmGroupPending
      ? {
          page: spmPage,
          codes: spmCodes,
        }
      : null;
  const { isShowSubscribeEmail } = useComplianceSubscriptionDisplay();
  const faceWidthRecommendation = resolveFaceWidthRecommendation(
    faceMeasurement,
    publicResourceData?.config_names?.product_filter_new?.find(
      (filter) => filter.attribute_code === 'product_size',
    )?.attribute_values ?? [],
  );
  const prepareNavigationScroll = useListNavigationScroll({
    sectionRef: listSectionRef,
    committedSearch: new URLSearchParams(searchParams || {}).toString(),
    isUpdating: isListUpdating,
    topOffset: isWeb ? 100 : 44,
    scopeKey: `${listId}@${currency.name}@${storeToken || 'guest'}`,
  });

  // 判断页面类型
  const isNewin = groupInfo?.page_template?.id === 3;
  const isAcc = groupInfo?.page_template?.id === 5;

  // 计算筛选总数
  const filterTotal = Object.keys(currentFilter).reduce((_total, key) => {
    const value = currentFilter[key];
    if (['temple', 'frame_width'].includes(key)) return _total;
    if (Array.isArray(value)) return _total + value.length;
    return value ? _total + 1 : _total;
  }, 0);

  // 神策埋点hook
  const {
    sdProductListPageADClick,
    sdProductListPageADExp,
    sdProductListPageBannerClick,
    sdProductListPageBannerExp,
    sdProductListPageRelatedGroupClick,
    sdProductListPageRelatedGroupExp,
    sdProductListPageSortBtnClick,
    sdProductListPageFilterSubmitBtnClick,
    SDProductListPageBtn_Click,
    SDProductListPageMkt_Click,
    sdProductListPageMktValidExp,
    SDProductListPageTryon_Click,
  } = useGoodsListTrack({
    groupInfo: spmGroupInfo,
    page: currentPage,
    limit,
    isWeb,
    imageRule,
    imageHoverRule,
    currentFilter,
    goodsListSpm: !!pathname?.startsWith('/goods-list/'),
  });

  const buildGoodsListApiParams = useCallback(
    (pageNum: number): IProductListApiParams => {
      const order = currentSort === '' || currentSort === DEFAULT_SORT_VALUE ? '' : currentSort;
      const topShopSkus =
        pageNum === 1 && searchParams?.adp ? searchParams.adp.split(',').slice(0, 6) : undefined;
      const apiParams: IProductListApiParams = {
        currency: currency.name,
        limit,
        page: pageNum,
        order,
        in_stock: 1,
        is_sale: 1,
        filter: transformFilterParams(currentFilter),
        append_correlation: 1,
        show_adv: 1,
        top_shop_skus: topShopSkus,
      };

      apiParams.group_path = String(listId);

      return apiParams;
    },
    [currency.name, currentFilter, currentSort, limit, listId, searchParams?.adp],
  );

  const preserveProductGridHeight = useCallback(() => {
    // 在商品节点被骨架屏替换前测量，连续操作时外层仍保留完整列表高度。
    setReservedGridHeight(productGridRef.current?.getBoundingClientRect().height ?? 0);
  }, []);

  const refreshGoodsListForIdentityChange = useCallback(async () => {
    const nextPage = isWeb ? currentPage : 1;
    const requestId = latestProductRequestIdRef.current + 1;
    latestProductRequestIdRef.current = requestId;
    const isLatestRequest = () => latestProductRequestIdRef.current === requestId;

    setGoodsListCache({});
    setNoData(false);
    preserveProductGridHeight();
    setIsDataUpdating(true);

    try {
      const response = (await fetchGoodsList(buildGoodsListApiParams(nextPage))) as unknown as {
        code: number;
        data: IProductListApiResponse;
      };
      if (!isLatestRequest()) return;

      if (response.code === 200 && response.data) {
        const nextList = toGoodsListItemsDto(response.data.list || []);
        const nextAllTotal = response.data.all_total || 0;

        if (response.data.group) setGroupInfo(response.data.group);
        setListData(nextList);
        setInfiniteScrollPage(nextPage);
        setTotal(response.data.total || 0);
        setAllTotal(nextAllTotal);
        setImageRule(response.data.image_rule);
        setImageHoverRule(response.data.hover_image_rule);
        setNoData(nextList.length === 0);
        setNoMoreShow(nextAllTotal <= nextPage * limit);
      }
    } catch (error) {
      if (isLatestRequest()) {
        logger.error('登录态变化刷新商品列表失败:', error);
      }
    } finally {
      if (isLatestRequest()) {
        setLoading(false);
        setIsDataUpdating(false);
      }
    }
  }, [
    buildGoodsListApiParams,
    currentPage,
    isWeb,
    limit,
    preserveProductGridHeight,
    setGoodsListCache,
  ]);

  // 更新缓存 key
  useEffect(() => {
    setGoodsListKey(goodsListKey);
  }, [goodsListKey, setGoodsListKey]);

  // 更新缓存数据
  useEffect(() => {
    if (!clientTokenReady) return;

    setGoodsListCache({
      infiniteScrollPage,
      listData,
      noMoreShow,
      column,
      expandedSections,
      showFilter,
    });
  }, [
    column,
    clientTokenReady,
    expandedSections,
    goodsListKey,
    infiniteScrollPage,
    listData,
    noMoreShow,
    setGoodsListCache,
    showFilter,
  ]);

  // 不含身份：同一查询的商品由身份请求和后续分页维护，RSC 只刷新外部 Footer。
  // 保留 URL page 与 adp，即使 WAP 身份刷新从第一页重新加载，也能识别查询切换。
  const listDataScope = useMemo(
    () =>
      JSON.stringify([buildGoodsListApiParams(currentPage), searchParams?.adp, seoFaqRendering]),
    [buildGoodsListApiParams, currentPage, searchParams?.adp, seoFaqRendering],
  );
  const clientOwnedListScopeRef = useRef<string | null>(null);
  const previousListDataScopeRef = useRef(listDataScope);

  useEffect(() => {
    if (previousListDataScopeRef.current === listDataScope) return;
    previousListDataScopeRef.current = listDataScope;
    clientOwnedListScopeRef.current = null;
    latestProductRequestIdRef.current += 1;
    setLoading(false);
  }, [listDataScope]);

  const refreshGoodsListAndSeoForIdentityChange = useCallback(() => {
    if (seoFaqRendering === 'external') clientOwnedListScopeRef.current = listDataScope;
    const request = refreshGoodsListForIdentityChange();
    // 刷新任务在首次 await 前递增请求 ID，捕获它以屏蔽过期请求的 RSC 刷新。
    const requestId = latestProductRequestIdRef.current;

    return runWithGoodsListSeoFooterRefresh(
      seoFaqRendering,
      () => {
        if (latestProductRequestIdRef.current === requestId) router.refresh();
      },
      () => request,
    );
  }, [listDataScope, refreshGoodsListForIdentityChange, router, seoFaqRendering]);

  // 监听initialData变化，更新客户端状态
  useEffect(() => {
    // 避免首次重复设置初始化数据
    if (initialDataRef.current === initialData) return;
    initialDataRef.current = initialData;
    const requestContextChanged = productRequestContextKeyRef.current !== productRequestContextKey;
    productRequestContextKeyRef.current = productRequestContextKey;
    if (requestContextChanged) {
      // 分类/分页/筛选等请求上下文切换后，旧身份或加载更多请求不得回写新列表。
      latestProductRequestIdRef.current += 1;
      setLoading(false);
    }
    // 已发出的 refresh 无法撤回；它的第一页不能覆盖当前身份的商品或已追加的分页。
    // Footer 仍直接使用新的 seoFooter props，不依赖这里同步商品状态。
    if (clientOwnedListScopeRef.current === previousListDataScopeRef.current) {
      return;
    }
    clientOwnedListScopeRef.current = null;
    latestProductRequestIdRef.current += 1;
    setLoading(false);
    const newGroupInfo = initialData.group;
    setGroupInfo(newGroupInfo);
    setListData(initialData.list);
    setTotal(initialData.total);
    setAllTotal(initialData.all_total);
    setImageRule(initialData.image_rule);
    setImageHoverRule(initialData.hover_image_rule);
    setNoData(initialData.noData);
    setNoMoreShow(initialData.noMoreShow);

    // 重置无限滚动页码为当前页码
    setInfiniteScrollPage(currentPage);

    // 服务端数据更新后，关闭loading状态
    setIsDataUpdating(false);
    setIsGlobalLoading(false);
  }, [initialData, currentPage, productRequestContextKey, setGroupInfo, setIsGlobalLoading]);

  // 必须位于 initialData 同步之后：分类与身份同时变化时，先使旧请求失效，再发起新身份请求。
  useUserIdentityChange(refreshGoodsListAndSeoForIdentityChange, clientTokenReady);

  useEffect(() => {
    if (!isNavigationPending && !isDataUpdating && reservedGridHeight !== undefined) {
      // 与服务端数据同步一起提交；退出占位后，导航定位 Hook 才能测量最终布局。
      setReservedGridHeight(undefined);
    }
  }, [isNavigationPending, isDataUpdating, reservedGridHeight]);

  // 同步临时筛选状态与当前筛选状态
  useEffect(() => {
    setTempFilter(currentFilter);
  }, [currentFilter]);

  // 初始化筛选项展开状态
  useEffect(() => {
    const product_filter_new = publicResourceData?.config_names?.product_filter_new || [];
    if (product_filter_new.length > 0) {
      const initialState: Record<string, boolean> = {};
      product_filter_new.forEach((item) => {
        initialState[item.attribute_code] = true;
      });
      setExpandedSections(initialState);
    }
  }, [publicResourceData]);

  // 构建新URL的工具函数
  const buildNewUrl = useCallback(
    (updates: { sort?: string; filter?: Record<string, any>; page?: number }) => {
      const newSearchParams = new URLSearchParams(searchParams || {});

      // 处理排序参数
      if (updates.sort !== undefined) {
        if (updates.sort && updates.sort !== DEFAULT_SORT_VALUE) {
          newSearchParams.set('sort', updates.sort);
        } else {
          newSearchParams.delete('sort');
        }
      }

      // 处理筛选参数
      if (updates.filter !== undefined) {
        newSearchParams.delete('filter');
        if (!isEmpty(updates.filter)) {
          const cleanFilter = {};
          Object.entries(updates.filter).forEach(([key, value]) => {
            if (value && key !== 'init') {
              cleanFilter[key] = value as string;
            }
          });
          if (!isEmpty(cleanFilter)) {
            newSearchParams.set('filter', JSON.stringify(cleanFilter));
          }
        }
      }

      // 处理页码参数
      if (updates.page !== undefined) {
        if (updates.page === 1) {
          newSearchParams.delete('page');
        } else {
          newSearchParams.set('page', updates.page.toString());
        }
      }

      const queryString = newSearchParams.toString();
      return `${window.location.pathname}${queryString ? `?${queryString}` : ''}`;
    },
    [searchParams],
  );

  // 滚动到列表区域
  const scrollToListSection = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      const listSection = listSectionRef.current;
      const num = isWeb ? 100 : 44;
      if (listSection) {
        const rect = listSection.getBoundingClientRect();
        const offsetTop = window.pageYOffset + rect.top - num;
        window.scrollTo({ top: offsetTop, behavior });
      }
    },
    [isWeb],
  );

  const navigateToList = (newUrl: string) => {
    prepareNavigationScroll(newUrl);
    preserveProductGridHeight();
    // 布局切换前先定位；若路由提交覆盖了位置，再由本次导航的单帧校验修正。
    scrollToListSection('instant');
    // 跟随导航生命周期结束加载态，同地址导航或中断也不会留下骨架屏。
    startNavigation(() => router.push(newUrl, { scroll: false }));
  };

  // 处理排序变化 - 通过URL变化触发服务端重渲染
  const handleSortChange = (sortValue: { value: string | number; sortName: string }) => {
    latestProductRequestIdRef.current += 1;
    setLoading(false);
    setIsDataUpdating(false);
    const newSort = sortValue.value.toString();
    const newUrl = buildNewUrl({ sort: newSort, page: 1 });

    navigateToList(newUrl);

    // 埋点
    sdProductListPageSortBtnClick(sortValue.sortName || DEFAULT_SORT_VALUE);
  };

  // 处理筛选变化 - 通过URL变化触发服务端重渲染
  const handleFilterChange = (newFilterResult: Record<string, any>) => {
    latestProductRequestIdRef.current += 1;
    setLoading(false);
    setIsDataUpdating(false);
    const newUrl = buildNewUrl({ filter: newFilterResult, page: 1 });

    navigateToList(newUrl);
  };

  // 处理分页变化 - 通过URL变化触发服务端重渲染
  const handlePageChange = (page: number) => {
    latestProductRequestIdRef.current += 1;
    setLoading(false);
    setIsDataUpdating(false);
    const newUrl = buildNewUrl({ page });

    navigateToList(newUrl);
  };

  // 处理无限滚动到底部（仅WAP端）
  const handleReachBottom = async () => {
    if (isWeb || loading || isDataUpdating || noMoreShow) return;

    const nextPage = infiniteScrollPage + 1;
    const requestId = latestProductRequestIdRef.current + 1;
    latestProductRequestIdRef.current = requestId;
    const isLatestRequest = () => latestProductRequestIdRef.current === requestId;

    setLoading(true);
    setIsShowFooterNav(false);

    try {
      const response = (await fetchGoodsList(buildGoodsListApiParams(nextPage))) as unknown as {
        code: number;
        data: IProductListApiResponse;
      };
      if (!isLatestRequest()) return;

      if (response.code === 200) {
        const { list = [], all_total } = response.data as IProductListApiResponse;
        const nextList = toGoodsListItemsDto(list);

        // 追加数据
        setListData((prev) => [...prev, ...nextList]);
        setInfiniteScrollPage(nextPage);
        const _noMoreShow = all_total <= nextPage * 36;
        setNoMoreShow(_noMoreShow);
      }
    } catch (error) {
      if (isLatestRequest()) {
        logger.error('无限滚动加载失败:', error);
      }
    } finally {
      if (isLatestRequest()) {
        setLoading(false);
      }
    }
  };

  // 筛选相关处理
  const handleFilterClick = useCallback(() => {
    // 打开筛选弹窗时，重置临时筛选状态为当前筛选状态
    setTempFilter(currentFilter);
    setShowFilter(true);
    !isWeb && SDProductListPageBtn_Click('FILTER');
  }, [currentFilter, SDProductListPageBtn_Click, isWeb]);

  const handleFilterClose = useCallback(() => {
    // 关闭弹窗时重置临时筛选状态
    setTempFilter(currentFilter);
    setShowFilter(false);
  }, [currentFilter]);

  const handleFilterApply = useCallback(() => {
    handleFilterChange(tempFilter);
    setShowFilter(false);
  }, [tempFilter, handleFilterChange]);

  const handleFilterValueChange = useCallback(
    (filterId: string, value: any, isPopup: boolean) => {
      if (isPopup) {
        // 弹窗模式：更新临时筛选状态，提供立即的视觉反馈
        setTempFilter((prev) => ({ ...prev, [filterId]: value }));
      } else {
        // 非弹窗模式：立即应用筛选
        const newFilterResult = { ...(currentFilter || {}), [filterId]: value };
        handleFilterChange(newFilterResult);
      }
    },
    [currentFilter, handleFilterChange],
  );

  // 列数变化
  const handleColumnChange = useCallback(
    (newColumn: number) => {
      setColumn(newColumn);
      if (listData.length > 0) {
        scrollToListSection();
      }
    },
    [scrollToListSection, listData],
  );

  // 切换筛选项展开状态
  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  // 商品点击事件
  const handleProductClick = (index: number, product: any) => {
    SDProductListPageMkt_Click(product, index);
    const productSceneName = '商品列表页商品坑位';
    setSession('zl_goodsDetailEnterPageScene', productSceneName);
  };

  // 渲染面包屑
  const renderBreadcrumbs = (className?: string, onBannerTop?: boolean) => {
    return (
      <Breadcrumbs
        items={[{ label: t('home'), href: '/' }, { label: groupInfo?.name || '' }]}
        className={`text-12 bg-white ${className || 'sticky top-[100px] z-3 h-[40px] pl-48 py-12 w-[288px]'}`}
        // Breadcrumbs 按当前路由解析列表页编码表后回传坑位编码；解析不到（如表外路由）不上报。
        SDProductDetailsPageBtnClick={(btnName: string, spmCnt?: string) => {
          if (spmCnt) SDProductListPageBtn_Click(btnName, spmCnt);
        }}
        sx={{
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap',
          },
          '& .MuiBreadcrumbs-li p': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: onBannerTop ? '100%' : '200px',
          },
          '& .MuiBreadcrumbs-li p, & .MuiBreadcrumbs-li a span, & .MuiBreadcrumbs-separator': {
            fontSize: '12px',
            color: waiMode ? colors.primaryBlack : colors.primaryText,
          },
          '& .MuiBreadcrumbs-li a': {
            display: 'block',
            fontSize: '12px',
            lineHeight: '1.5',
          },
        }}
      />
    );
  };

  // 渲染筛选组件
  const renderGoodsFilter = useCallback(
    (isPopup = false) => {
      // 弹窗模式使用临时筛选状态，非弹窗模式使用当前筛选状态
      const filterValues = isPopup ? tempFilter : currentFilter;

      return (
        <GoodsFilter
          faceWidthRecommendation={faceWidthRecommendation}
          onClear={() => {
            if (isPopup) {
              // 弹窗模式：清空临时筛选状态
              setTempFilter({});
            } else {
              // 非弹窗模式：立即清空筛选
              handleFilterChange({});
            }
          }}
          values={filterValues}
          onValueChange={(filterId, value) => handleFilterValueChange(filterId, value, isPopup)}
          isWeb={isWeb}
          expandedSections={expandedSections}
          onToggleSection={toggleSection}
          isPopup={isPopup}
          sdProductListPageFilterSubmitBtnClick={sdProductListPageFilterSubmitBtnClick}
          {...(isPopup ? { onApply: handleFilterApply } : {})}
        />
      );
    },
    [
      tempFilter,
      currentFilter,
      handleFilterValueChange,
      handleFilterApply,
      handleFilterChange,
      isWeb,
      expandedSections,
      toggleSection,
      faceWidthRecommendation,
      sdProductListPageFilterSubmitBtnClick,
    ],
  );

  const showBottomRelatedLink = useMemo(() => {
    const flag1 = isWeb || (!isWeb && noMoreShow);
    const flag2 = groupInfo?.related_block_link && groupInfo?.related_block_link?.length > 0;
    return flag1 && flag2;
  }, [isWeb, noMoreShow, groupInfo?.related_block_link]);
  const legacySeoGroupInfo = useMemo(
    () => createLegacyGoodsListSeoGroup(groupInfo, seoFaqRendering),
    [groupInfo, seoFaqRendering],
  );
  const shouldRenderLegacySeoFaq = seoFaqRendering === 'legacy';
  const hasFaqContent =
    shouldRenderLegacySeoFaq && (!!groupInfo?.h_one || !!groupInfo?.faq_list?.length);

  useEffect(() => {
    if (!isWeb && noMoreShow) {
      setIsShowFooterNav(noMoreShow);
    }
  }, [noMoreShow]);

  const isClient = useClientOnly();

  useEffect(() => {
    if (!isClient || checkedScrollListIdRef.current === listId) {
      return;
    }

    checkedScrollListIdRef.current = listId;
    const { group_id } = getGroupInfo();
    if (`${group_id}` !== `${listId}` && (isWeb || (!isWeb && infiniteScrollPage === 1))) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [infiniteScrollPage, isClient, isWeb, listId]);

  const pageContent = (
    <div>
      <div className={`relative ${isWeb ? 'w-full min-h-screen flex' : ''}`}>
        {/* 左侧筛选区域 - PC端 */}
        {isWeb && !isAcc && (
          <div className="max-[920px]:hidden">
            {renderBreadcrumbs('', false)}
            <div className="w-[304px] left-0 z-[2] h-screen sticky top-[140px] transition-transform duration-10">
              {/* 增加overscrollBehavior属性，阻止滚动事件向父级容器传播，防止滚动穿透，导致出现左侧滚动到底之后，右侧商品列表开始滚动 */}
              <div
                className="h-full bg-white overflow-y-auto show-scrollbar-when-hover"
                style={{ overscrollBehavior: 'contain' }}
              >
                <p className="pt-[16px] text-20 leading-[24px] font-bb pb-24 pl-48">
                  {t('Filters')}
                </p>
                <div className="pl-48 pr-24 pb-[350px]">{renderGoodsFilter(false)}</div>
                <div className="pc-filter-bottom-anchor" />
              </div>
            </div>
          </div>
        )}

        {/* 右侧内容区域 */}
        <div
          className={`
           goods-list
           ${isWeb && !isAcc ? 'min-h-screen w-[calc(100vw-304px)] max-[920px]:w-full' : ''}
           ${isAcc && isWeb ? 'w-full max-w-[1640px] mx-auto' : ''}
          `}
        >
          <div className={`${isWeb ? 'px-40 pb-40' : ''} ${isAcc && isWeb ? 'mx-auto' : ''}`}>
            {/* 移动端面包屑 */}
            {isWeb && (
              <div className="min-[920px]:hidden">{renderBreadcrumbs('pt-16 w-[100%]', true)}</div>
            )}

            {/* Group Banner */}
            <GroupBanner
              groupInfo={groupInfo}
              onBannerClick={sdProductListPageBannerClick}
              onBannerExp={sdProductListPageBannerExp}
              onRelatedGroupClick={(item: any, index: number) => {
                sdProductListPageRelatedGroupClick(item, index, '顶部相关导航位');
                setSession('zl_goodsListEnterPageScene', '商品列表页顶部相关导航位');
              }}
              onRelatedGroupExp={(item: any, index: number) => {
                sdProductListPageRelatedGroupExp(item, index, '顶部相关导航位');
              }}
              isWeb={isWeb}
            />

            {/* 主要内容区域 */}
            <section ref={listSectionRef} className="list-section">
              {/* 排序筛选栏 */}
              <SortFilterBar
                listId={listId}
                currentSort={currentSort}
                onSortChange={handleSortChange}
                filterTotal={filterTotal}
                filterDrawerOpen={showFilter}
                filterDrawerContentId={filterDrawerContentId}
                onFilterClick={handleFilterClick}
                showColumnControl
                column={column}
                onColumnChange={handleColumnChange}
                isAcc={isAcc}
                groupInfo={groupInfo}
                isNewin={isNewin || groupInfo?.page_template?.id === 6}
                isWeb={isWeb}
                total={total}
                onSortBtnClick={() => {
                  SDProductListPageBtn_Click('SORT BY');
                }}
              />

              {/* 商品列表 / 内联骨架屏 */}
              <div
                ref={productGridRef}
                aria-busy={isListUpdating}
                style={{ minHeight: isListUpdating ? reservedGridHeight : undefined }}
              >
                {isListUpdating ? (
                  <InlineProductGridSkeleton isWeb={isWeb} column={column} />
                ) : (
                  <ProductList
                    listData={listData}
                    total={total}
                    allTotal={allTotal}
                    noData={noData}
                    loading={loading}
                    isWeb={isWeb}
                    column={column}
                    isAcc={isAcc}
                    imageRule={imageRule}
                    imageHoverRule={imageHoverRule}
                    infiniteScrollPage={infiniteScrollPage}
                    limit={limit}
                    handleReachBottom={handleReachBottom}
                    page={currentPage}
                    handlePageChange={handlePageChange}
                    handleProductClick={handleProductClick}
                    handleProductValidInView={sdProductListPageMktValidExp}
                    sdProductListPageADExp={sdProductListPageADExp}
                    sdProductListPageADClick={sdProductListPageADClick}
                    SDProductListPageTryon_Click={SDProductListPageTryon_Click}
                    currentFilter={currentFilter}
                    mobileProductInsert={isShowSubscribeEmail ? <SubscribeBar /> : undefined}
                  />
                )}
              </div>
            </section>
            {isWeb && isShowSubscribeEmail && allTotal > limit && (
              <SubscribeBar className="mt-48" />
            )}

            {/* 底部内容 相关链接 + 底部FAQ - 移动端 (爬虫SSR / 普通用户CSR) */}
            {isBot ? (
              <GoodsListSeoBlock
                groupInfo={legacySeoGroupInfo}
                isWeb={isWeb}
                noMoreShow={noMoreShow}
                showBottomRelatedLink={showBottomRelatedLink}
                sdProductListPageRelatedGroupClick={sdProductListPageRelatedGroupClick}
                sdProductListPageRelatedGroupExp={sdProductListPageRelatedGroupExp}
                isBot={isBot}
              />
            ) : (
              <ClientSeoBlockWrapper
                groupInfo={legacySeoGroupInfo}
                isWeb={isWeb}
                noMoreShow={noMoreShow}
                showBottomRelatedLink={showBottomRelatedLink}
                sdProductListPageRelatedGroupClick={sdProductListPageRelatedGroupClick}
                sdProductListPageRelatedGroupExp={sdProductListPageRelatedGroupExp}
              />
            )}
          </div>
        </div>
      </div>

      {/* 底部FAQ - PC端 (爬虫SSR / 普通用户CSR) */}
      {shouldRenderLegacySeoFaq &&
        (isBot ? hasFaqContent : groupInfo?.h_one) &&
        isWeb &&
        (isBot ? (
          <FaqPart groupInfo={groupInfo} isWeb={isWeb} defaultExpandAll defaultQuestionLevel={4} />
        ) : (
          <ClientFaqPartWrapper groupInfo={groupInfo} isWeb={isWeb} />
        ))}

      {seoFooter && (
        <GoodsListSeoFooterSlot isBot={isBot} isWeb={isWeb} noMoreShow={noMoreShow}>
          {seoFooter}
        </GoodsListSeoFooterSlot>
      )}

      {/* PC端筛选弹窗 */}
      {isWeb && (
        <Popup
          open={showFilter}
          onClose={handleFilterClose}
          position="left"
          width="482px"
          round={false}
          title={t('Filters')}
          contentContainerId={filterDrawerContentId}
          bodyClassName="!pt-0 !px-32"
          sx={{
            '& .popup-title': {
              textAlign: 'left',
              border: 'none',
              paddingInline: '32px !important',
              paddingBlock: '26px !important',
              lineHeight: '24px',
              '& .popup-title-text': {
                fontSize: '20px !important',
              },
            },
          }}
        >
          {renderGoodsFilter(true)}
        </Popup>
      )}

      {/* 移动端筛选弹窗 */}
      {!isWeb && (
        <Popup
          open={showFilter}
          onClose={handleFilterClose}
          position="right"
          width="335px"
          round={false}
          title={t('Filters')}
          contentContainerId={filterDrawerContentId}
          bodyClassName="!pt-0"
        >
          {renderGoodsFilter(true)}
        </Popup>
      )}
    </div>
  );

  return spmScope ? (
    <SpmScopeProvider page={spmScope.page} codes={spmScope.codes}>
      {pageContent}
    </SpmScopeProvider>
  ) : (
    pageContent
  );
}
